import { create } from 'zustand';
import { forgetBrand } from '@/lib/brand';
import {
  api,
  ApiError,
  getToken,
  setSession,
  type Profile,
  type Room,
  type ProfilePatch,
  type SignedIn,
  type LeadFields,
} from '@/lib/api';

interface AuthState {
  token: string | null;
  profile: Profile | null;
  room: Room | null;
  loading: boolean;
  error: string | null;
  /** The server's error code for the last failed sign-in (e.g. RESIDENT_NOT_INVITED). */
  errorCode: string | null;
  isAuthenticated: boolean;
  verifyLink: (token: string) => Promise<boolean>;
  googleSignIn: (credential: string, lead?: Pick<LeadFields, 'consentVersion' | 'marketingOptIn'>) => Promise<boolean>;
  hydrate: () => Promise<void>;
  save: (patch: ProfilePatch) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useAuth = create<AuthState>((set) => {
  // Start a session (magic link or Google). On failure, never fall back to
  // whatever session this device held before: it may be someone else's on a
  // shared phone.
  const start = async (signIn: () => Promise<SignedIn>): Promise<boolean> => {
    set({ loading: true, error: null, errorCode: null });
    try {
      const { accessToken, refreshToken, resident, room } = await signIn();
      setSession({ accessToken, refreshToken });
      set({ token: accessToken, profile: resident, room, isAuthenticated: true, loading: false });
      return true;
    } catch (e) {
      setSession(null);
      set({
        token: null,
        profile: null,
        room: null,
        error: (e as Error).message,
        errorCode: e instanceof ApiError ? (e.code ?? null) : null,
        loading: false,
        isAuthenticated: false,
      });
      return false;
    }
  };

  return {
    token: getToken(),
    profile: null,
    room: null,
    loading: false,
    error: null,
    errorCode: null,
    isAuthenticated: !!getToken(),

    // Consume a magic-link token from the invite email.
    verifyLink: (token: string) => start(() => api.verify(token)),

    // Google sign-in for a resident the housing team already invited.
    googleSignIn: (credential, lead) => start(() => api.google(credential, lead)),

    // Re-load profile + room for an already-signed-in resident.
    hydrate: async () => {
      if (!getToken()) {
        set({ isAuthenticated: false });
        return;
      }
      set({ loading: true });
      try {
        const [profile, roomRes] = await Promise.all([api.me(), api.room()]);
        set({ profile, room: roomRes.room, isAuthenticated: true, loading: false });
      } catch {
        // The API client already tried to renew; a session it could not keep is over.
        if (!getToken()) set({ token: null, profile: null, isAuthenticated: false, loading: false });
        else set({ loading: false, error: 'אין חיבור כרגע. בדקו את האינטרנט ונסו שוב.' });
      }
    },

    save: async (patch: ProfilePatch) => {
      const profile = await api.updateMe(patch);
      set({ profile });
    },

    logout: () => {
      // End this device's session on the server too (best effort), then forget it,
      // including any unsaved form drafts (a shared phone keeps nobody's details).
      void api.signOut().catch(() => undefined);
      try {
        for (const k of Object.keys(localStorage)) if (k.startsWith('rooms_draft_')) localStorage.removeItem(k);
      } catch {
        /* storage unavailable */
      }
      // Nor which building they live in: the next person sees TendedLoop's sign-in.
      forgetBrand();
      setSession(null);
      set({ token: null, profile: null, room: null, isAuthenticated: false });
    },

    clearError: () => set({ error: null, errorCode: null }),
  };
});

export type { Profile, Room };
